import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'

import {
  AccountHasPaymentProvidersDocument,
  GetShopifySubscriptionStatusDocument,
  GetShopifySubscriptionStatusQuery,
  ListConnectionPriceTypesORDocument,
  OrderHasRemainingBalanceDocument,
  OrderRequestStatusDocument,
  OrderReviewGigaQueryDocument,
  OrderReviewGigaQueryQuery,
  OrderReviewGigaQueryQueryVariables,
  OrderStatus,
  RequestStatus,
  ShopifyAppName,
  UseJoorPayFlagDocument,
  VariantFilterValuesInput,
} from '__generated__/atlas-types'

import { OrderQueryResultWithPartialError } from 'types/apollo'
import {
  relayConnectionToArray,
  toGlobalId,
} from 'utils/transformations/graphql'

import { GraphQLError } from 'graphql'
import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import { getAccountId, isAccountTypeBrand } from 'store/currentUser/selectors'

import {
  formatProducts,
  formatSummaryAmounts,
} from './components/Overview/components/ProductsWidget/formatters'
import { useOrderId, useOrdersQuery } from 'features/Orders/orders.hooks'

atlas`#graphql
  query OrderGeneralInfo($filters: OrderFilterInput!) {
    orders(filters: $filters) {
      edges {
        node {
          id
          warehouses {
            edges {
              node {
                id
              }
            }
          }
          retailerProfile {
            id
            displayName
          }
          collections {
            edges {
              node {
                id
                name
              }
            }
          }
          priceType {
            id
          }
          doors {
            edges {
              node {
                id
                name
                code
              }
            }
          }
        }
      }
    }
  }

  query OrderReviewGigaQuery(
    $filters: OrderFilterInput!,
    $traitCodes: VariantFilterValuesInput!,
    $limit: Int,
    $offset: Int
  ) {
    orders(filters: $filters) {
      edges {
        node {
          id
          created
          name
          internalComments
          isOrphan
          modified
          orderComments
          quantity
          referenceNumber
          status
          statusModified
          webOrderLookupId
          wholesaleTotal

          brand {
            id
            name
            orderSettings {
              ...BrandOrderSettings
            }
          }

          buyer {
            id
            name
            email
          }

          orphanRetailer {
            id
            retailerName
            retailerEmail
          }

          collections {
            ...OrderCollections
          }

          divisions {
            id
            name
            code
          }

          event {
            id
            name
            code
          }

          financials {
            ...OrderFinancials
          }

          lastModifier {
            byAdmin
            email
          }

          priceType {
            id
            name
            currency {
              id
              name
              code
              symbol
              decimals
              decimal_separator
              thousands_separator
            }
            retailCurrency {
              id
              code
            }
          }

          retailerProfile {
            id
            name
            displayName
            code
          }

          salesComissions {
            name
          }

          salesRepresentative {
            id
            displayName
            email
          }

          season {
            id
            name
            code
            year
          }

          shipping {
            ...OrderShippingDetails
          }

          tax {
            ...OrderTax
          }

          termsAndConditions {
            id
            agreesWithTerms
            terms
            platform
            dateModified
            name
            title
          }

          types {
            id
            name
            code
          }

          warehouses {
            edges {
              node {
                id
                name
                code
              }
            }
          }

          products(filters: {
            pagination: {
              limit: $limit,
              offset: $offset
            }
          }) {
            ...ProductsInfo
          }
        }

        checkMinimums {
          orderAmount
          orderQuantity
          productMinimums
          colorMinimums
        }
      }
    }
  }

  query OrderRequirementChecks($orderIds: [ID!]!) {
    orderRequirementChecks(orderIds: $orderIds) {
      id
      checksMinimums
      sizeRunChanged
    }
  }

  query AccountHasPaymentProviders($destinationAccountId: ID!) {
    accountPaymentProviders(destinationAccountId: $destinationAccountId) {
      accountId
      paymentProvider
      retailersCanSeeInvoices
    }
  }

  query OrderRequestStatus($filters: OrderFilterInput!) {
    orders(filters: $filters) {
      edges {
        node {
          id
          paymentInfo {
            requestStatus
          }
        }
      }
    }
  }
`
export const PRODUCTS_LIMIT = 20
export enum OrderErrors {
  ORDER_DOESNT_EXIST = 'ORDER_DOESNT_EXIST',
  ORDER_HAS_PARTIAL_ERROR = 'ORDER_HAS_PARTIAL_ERROR',
}

export const useInvoiceHasBeenSent = () => {
  const orderId = useOrderId()
  const { data, loading } = useQuery(OrderRequestStatusDocument, {
    client: atlasClient,
    variables: {
      filters: {
        ids: [orderId as string],
      },
    },
    skip: !orderId,
  })

  const hasInvoiceBeenSent =
    relayConnectionToArray(data?.orders)?.[0]?.paymentInfo?.requestStatus ===
    RequestStatus.SENT

  return {
    invoicesSentLoading: loading,
    hasInvoiceBeenSent,
  }
}

export const useFullOrderInfo = () => {
  const orderId = useOrderId()
  const { data, loading, refetch, errors, fetchMore } = useOrdersQuery(
    OrderReviewGigaQueryDocument,
    {},
    {
      errorPolicy: 'all',
      skip: !orderId,
      variables: {
        filters: { ids: [orderId] },
        traitCodes: VariantFilterValuesInput.COLOR,
        limit: 20,
        offset: 0,
      },
      fetchPolicy: 'network-only',
    },
  ) as OrderQueryResultWithPartialError<
    OrderReviewGigaQueryQuery,
    OrderReviewGigaQueryQueryVariables
  >

  const response =
    (useMemo(
      () => relayConnectionToArray(data?.orders)[0] || {},
      [data?.orders],
    ) as OrderReviewGigaQueryQuery['orders']['edges'][0]['node']) || {}

  const error = useMemo(() => {
    if (errors && hasPriceTypeError(errors)) {
      return OrderErrors.ORDER_HAS_PARTIAL_ERROR
    }

    if (!loading && data?.orders.edges.length === 0) {
      return OrderErrors.ORDER_DOESNT_EXIST
    }

    return undefined
  }, [errors, loading, data])

  if (error && !data) {
    console.error('[OR23 OVERVIEW] > OrderReviewGigaQueryQuery', error)
  }

  const currencyCode =
    data?.orders.edges?.[0]?.node.priceType?.currency?.code ?? ''
  const retailCurrencyCode =
    data?.orders.edges?.[0]?.node.priceType?.retailCurrency?.code ?? ''
  const orderStatus = data?.orders.edges?.[0]?.node.status ?? null
  const productAmounts = formatSummaryAmounts(data)
  const products = formatProducts({ data, currencyCode, retailCurrencyCode })

  const fetchAllProducts = (params: { offset?: number; limit?: number }) =>
    fetchMore({
      variables: { offset: params?.offset ?? 20, limit: params?.limit ?? 0 },
    })

  return {
    loading,
    orderFullInfo: {
      ...response,
      orderStatus,
    },
    products,
    productAmounts,
    fetchAllProducts,
    refetch,
    error,
  }
}

export type Retailer = {
  retailerAccount: {
    id: string
  }
}

export type Collection = {
  id: string
  code: string
  name: string
  visibility: string
  visibleToViewer: boolean
  archived: boolean
  retailers: Retailer[]
  delivery?: {
    minimumShippingWindow: number
  }
  deliveryWindow?: {
    minStartShipDate: Date
    maxCompleteShipDate: Date
  }
}

export type OrderInternalComments = {
  id: string
  status: OrderStatus
  internalComments: string
}

export type useOrderInternalCommentsReturnType = {
  order?: OrderInternalComments
  loading?: boolean
}

const hasPriceTypeError = (errors: ReadonlyArray<GraphQLError>) =>
  errors.some((error) =>
    error?.path?.some((pathLevel) => pathLevel === 'priceType'),
  )

export function useAccountPaymentProvidersQuery(brandId?: string) {
  const { data, loading } = useQuery(AccountHasPaymentProvidersDocument, {
    client: atlasClient,
    variables: {
      destinationAccountId: brandId!,
    },
    skip: !brandId,
  })
  return { data, loading }
}

atlas`#graphql
  query UseJoorPayFlag($brandAccountId: ID!) {
    public {
      useJoorPay(brandAccountId: $brandAccountId)
    }
  }
`

export const useJoorPayUseReachFlag = (brandId?: string, skip?: boolean) => {
  const { data, loading } = useQuery(UseJoorPayFlagDocument, {
    client: atlasClient,
    variables: {
      brandAccountId: brandId || '',
    },
    skip: !brandId || skip,
  })

  return { data, loading }
}

atlas`#graphql
  query ListConnectionPriceTypesOR($input: ListConnectionPriceTypeInput!) {
    listConnectionPriceTypes(input: $input) {
      id
      priceType {
        id
        name
        currency {
          id
          name
          code
        }
        retailCurrency {
          id
        }
      }
    }
  } 
`

export const useOrderPriceTypes = ({
  retailerId,
  orderId,
  skip,
}: {
  retailerId?: string
  orderId?: string
  skip?: boolean
}) => {
  const { data } = useQuery(ListConnectionPriceTypesORDocument, {
    client: atlasClient,
    variables: {
      input: {
        retailerId: retailerId!,
        orderId,
      },
    },
    skip,
  })

  return { data }
}

atlas`#graphql
  query OrderHasRemainingBalance($filters: OrderFilterInput!) {
    orders(filters: $filters) {
      edges {
        node {
          id
          paymentInfo {
            remainingBalance
          }
        }
      }
    }
  }
`

export const useOrderHasRemainingBalance = () => {
  const orderId = useOrderId()
  const { data, loading } = useQuery(OrderHasRemainingBalanceDocument, {
    client: atlasClient,
    variables: {
      filters: {
        ids: [orderId as string],
      },
    },
    skip: !orderId,
  })

  const remainingBalance: number = Number(
    relayConnectionToArray(data?.orders)?.[0]?.paymentInfo?.remainingBalance ||
      0,
  )

  return {
    hasRemainingBalance: Boolean(remainingBalance),
    loading,
  }
}

atlas`#graphql
  query GetShopifySubscriptionStatus($accountId: ID!, $appName: ShopifyAppName!) {
    shopifyIntegrations(accountId: $accountId, appName: $appName) {
      id
      isSubscriptionActive
      storeCurrencyCode
    }
  }
`

export const useShopifySubscriptionStatus = () => {
  const accountId = useSelector(getAccountId)
  const isBrand = useSelector(isAccountTypeBrand)
  const formattedAccountId = toGlobalId('Account', accountId) || ''
  const { data } = useQuery<GetShopifySubscriptionStatusQuery>(
    GetShopifySubscriptionStatusDocument,
    {
      client: atlasClient,
      skip: !formattedAccountId || isBrand,
      variables: {
        accountId: formattedAccountId,
        appName: ShopifyAppName.RETAILER_PRODUCT_SYNC,
      },
    },
  )
  const shopifyIntegration = data?.shopifyIntegrations?.[0]
  return {
    isSubscriptionActive: shopifyIntegration?.isSubscriptionActive,
    shopifyStoreCurrencyCode: shopifyIntegration?.storeCurrencyCode,
  }
}
